import type {
  Building,
  BuildingDesign,
} from '@shared/models/design/building-design.model';
import { isEmptyBuildingGeometry } from '@shared/helpers/design/storey.helpers';
import {
  applyScaleBuildingToGfa,
  type ScaleBuildingToGfaStatus,
} from '@shared/helpers/design/design.helpers';
import { mutateDesign } from '@shared/helpers/design/mutators/design.mutator';
import { toDesignSnapshotPersistShape } from './design-snapshot-persist.helpers';

/** Add the ephemeral candidate to the design consumed by Design-tab UI. */
export const withBuildingCandidate = (
  design: BuildingDesign,
  candidate: Building | undefined,
): BuildingDesign => {
  if (!candidate || design.buildings.some(({ id }) => id === candidate.id)) {
    return design;
  }
  return { ...design, buildings: [...design.buildings, candidate] };
};

export type BuildingCandidatePersistence = {
  design: BuildingDesign;
  shouldClearCandidate: boolean;
};

type BuildingCandidatePersistenceOptions = {
  /** The candidate already belongs to persisted project data. */
  preserveEmptyCandidate?: boolean;
};

export type BuildingCandidateTargetGfaResult = {
  design: BuildingDesign;
  status: ScaleBuildingToGfaStatus;
};

/** Whether the design contains exactly one building and it has no footprint. */
export const hasSoleEmptyBuilding = (design: BuildingDesign): boolean =>
  design.buildings.length === 1 &&
  Boolean(design.buildings[0] && isEmptyBuildingGeometry(design.buildings[0]));

/**
 * Adopt the sole empty building of a blank project into the focused candidate
 * flow. A sentinel or another explicit building id means the user intentionally
 * chose a different panel state and must not be overridden.
 */
export const getInitialEmptyBuildingCandidate = (
  design: BuildingDesign,
  selectedBuildingId: string | undefined,
  currentCandidate: Building | undefined,
): Building | undefined => {
  if (currentCandidate || !hasSoleEmptyBuilding(design)) return undefined;
  const building = design.buildings[0];
  if (!building) return undefined;
  if (selectedBuildingId !== undefined && selectedBuildingId !== building.id) {
    return undefined;
  }
  return building;
};

/** Clone the completed ground storey to the count chosen before drawing. */
export const applyBuildingCandidateStoreyCount = (
  design: BuildingDesign,
  candidate: Building | undefined,
  storeyCount: number | undefined,
): BuildingDesign => {
  if (
    !candidate ||
    !Number.isSafeInteger(storeyCount) ||
    !storeyCount ||
    storeyCount < 1
  ) {
    return design;
  }
  const current = design.buildings.find(({ id }) => id === candidate.id);
  if (
    !current ||
    isEmptyBuildingGeometry(current) ||
    current.storeys.length >= storeyCount
  ) {
    return design;
  }

  let next = design;
  for (let index = current.storeys.length; index < storeyCount; index += 1) {
    next = mutateDesign.storey.add(next, candidate.id);
  }
  return next;
};

/** Apply a GFA entered while the new building still had no footprint. */
export const applyBuildingCandidateTargetGfa = (
  design: BuildingDesign,
  candidate: Building | undefined,
  targetGfa: number | undefined,
): BuildingCandidateTargetGfaResult => {
  if (!candidate || !targetGfa) return { design, status: 'unchanged' };
  const current = design.buildings.find(({ id }) => id === candidate.id);
  if (!current || isEmptyBuildingGeometry(current)) {
    return { design, status: 'unchanged' };
  }
  return applyScaleBuildingToGfa(design, targetGfa, candidate.id);
};

/**
 * Keep an ephemeral empty candidate out of persisted project data. A candidate
 * adopted from persisted data remains there while its first footprint is being
 * placed. Once a candidate has geometry, mark it as promoted.
 */
export const resolveBuildingCandidatePersistence = (
  design: BuildingDesign,
  candidate: Building | undefined,
  options: BuildingCandidatePersistenceOptions = {},
): BuildingCandidatePersistence => {
  if (!candidate) return { design, shouldClearCandidate: false };
  const current = design.buildings.find(({ id }) => id === candidate.id);
  if (!current) return { design, shouldClearCandidate: true };
  if (!isEmptyBuildingGeometry(current)) {
    return { design, shouldClearCandidate: true };
  }
  if (options.preserveEmptyCandidate) {
    return { design, shouldClearCandidate: false };
  }
  return {
    design: {
      ...design,
      buildings: design.buildings.filter(({ id }) => id !== candidate.id),
    },
    shouldClearCandidate: false,
  };
};

export type SubmittedDesignSnapshot = {
  design: BuildingDesign;
  shouldClearCandidate: boolean;
};

type SubmittedDesignSnapshotOptions = {
  candidate: Building | undefined;
  storeyCount: number | undefined;
  targetGfa: number | undefined;
  /** The candidate already belongs to persisted project data. */
  preserveEmptyCandidate: boolean;
};

/**
 * The design snapshot a save actually writes.
 * Applies the candidate's storey count and target GFA, drops an empty
 * unpreserved candidate, then {@link toDesignSnapshotPersistShape}.
 * Underlay persist checks must expect this shape: scaling one building in a
 * multi-building design moves the combined footprint center and translates
 * underlay centers with it (DEV-3835).
 */
export const toSubmittedDesignSnapshot = (
  design: BuildingDesign,
  options: SubmittedDesignSnapshotOptions,
): SubmittedDesignSnapshot => {
  const withStoreys = applyBuildingCandidateStoreyCount(
    design,
    options.candidate,
    options.storeyCount,
  );
  const { design: withGfa } = applyBuildingCandidateTargetGfa(
    withStoreys,
    options.candidate,
    options.targetGfa,
  );
  const persistence = resolveBuildingCandidatePersistence(
    withGfa,
    options.candidate,
    {
      preserveEmptyCandidate: options.preserveEmptyCandidate,
    },
  );
  return {
    design: toDesignSnapshotPersistShape(persistence.design),
    shouldClearCandidate: persistence.shouldClearCandidate,
  };
};
